import {
  AccessorFnColumnDef,
  CellContext,
  ColumnFilter,
  ColumnFiltersState,
  Table,
  VisibilityState,
} from '@tanstack/react-table';
import { Draft, produce, WritableDraft } from 'immer';
import _ from 'lodash';
import React from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa6';
import { createUseStyles } from 'react-jss';
import { useImmer } from 'use-immer';

import { cellFactory } from '@/components/available-preview-list/aplUtils';
import PaginatedPreviewTable from '@/components/paginated-preview-table/PaginatedPreviewTable';
import PreviewTableHeader from '@/components/preview-table-header/PreviewTableHeader';
import TableHeader from '@/components/table-header/TableHeader';
import TableHeaderSelectAll from '@/components/table-header-select-all/TableHeaderSelectAll';
import { DEFAULT_STRING_ARRAY_COMPARE_ASCENDING, DEFAULT_STRING_ARRAY_COMPARE_DESCENDING } from '@/constants/defaults';
import { TableOrderDesc, ViewEditorDrawerMode } from '@/constants/enums';
import { useAppSelector } from '@/store/hooks';
import { APLItem, CellColorUpdate, UniqueId } from '@/types/ui.types';
import SortingUtils from '@/utils/sorting';
import { getId } from '@/utils/validators';

enum ColumnFilterStateProperty {
  AvailableItemsColumnFilters = 'availableItemsColumnFilters',
  PreviewItemsColumnFilters = 'previewItemsColumnFilters',
}

type TableColumnIds = { [key: string]: string };

type ItemMap<T> = Map<UniqueId, APLItem<T>>;

/*
 * To prevent multiple state updates while keeping relevant filters centralized to a single location,
 * we need to differentiate between internal and external filters to prevent overwriting or omitting filters which
 * should be handled internally by the component.
 *
 * Internal filters are not affected by filters passed in via props, IFF the internal filters are marked as such. (This is up to you to specify)
 *
 * This should really only apply to "available" items (left-hand side), as the preview items are simply a result of user selection and
 * are generally unaffected by external filters. However, for the sake of consistency, we will type both available and preview item filter states, as CustomColumnFilterState.
 * */
interface CustomColumnFilter extends ColumnFilter {
  internal?: boolean;
}

type CustomColumnFiltersState = CustomColumnFilter[];

interface ITableState<T> {
  // React Table column def appeasement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableColumns: AccessorFnColumnDef<APLItem<T>, any>[];
  availableItemsColumnFilters: CustomColumnFiltersState;
  previewColumns: AccessorFnColumnDef<APLItem<T>>[];
  previewItemsColumnFilters: CustomColumnFiltersState;
  selectedAvailableRowIds: Set<UniqueId>;
  selectedPreviewRowIds: Set<UniqueId>;
  tableOrderDesc: TableOrderDesc;
  unfilteredViewIds: number[];
}

interface IAvailablePreviewListProps<T> {
  // React Table column def appeasement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly availableColumns: AccessorFnColumnDef<APLItem<T>, any>[];
  readonly availableItems: ItemMap<T>;
  readonly availableItemsPrimaryColumnId: string;
  readonly availableItemsTableLabel: string;
  readonly availableRowUniqueKey: string;
  readonly isOpen: boolean;
  readonly onItemsChanged: (updatedData: T[]) => void;
  // React Table column def appeasement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly previewColumns: AccessorFnColumnDef<APLItem<T>, any>[];
  readonly previewItems: ItemMap<T>;
  readonly previewItemsPrimaryColumnId: string;
  readonly previewItemsTableLabel: string;
  readonly previewRowUniqueKey: string;
  readonly primaryAvailableColumnIndex: number;
  readonly primaryPreviewColumnIndex: number;
  readonly tableColumnIds: TableColumnIds;
  readonly availableColumnsVisibility?: VisibilityState;
  readonly availableItemsColumnFilters?: ColumnFiltersState;
  readonly availableItemsTableNoDataFoundMessage?: string;
  readonly cellContentInjector?: (item: CellContext<APLItem<T>, unknown>) => React.JSX.Element;
  readonly injectedContentOverridesText?: boolean;
  readonly isLoading?: boolean;
  readonly overrideSorting?: boolean;
  readonly previewColumnsVisibility?: VisibilityState;
  readonly showColorPicker?: boolean;
  readonly showSideControls?: boolean;
  readonly topPosition?: string;
}

const useSharedRowStyles = createUseStyles({
  row: {
    '&:hover': {
      backgroundColor: '#E2E8F0',
      color: 'white',
    },
    cursor: 'pointer',
  },
});

const usePreviewRowStyle = createUseStyles({
  icon: {
    '&:hover': {
      backgroundColor: '#3182ce',
      borderRadius: '5px',
      color: '#fff',
      // eslint-disable-next-line sort-keys
      '&.delete': {
        backgroundColor: '#e53e3e',
      },
    },
    color: '#5b5757',
    cursor: 'pointer',
    height: '26px',
    padding: '5px',
    width: '26px',
  },
});

const clearColumnFilter = <T,>(
  columnName: keyof TableColumnIds,
  filterStateDraft: WritableDraft<ITableState<T>>,
  columnFilterProperty: ColumnFilterStateProperty,
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  filterStateDraft[columnFilterProperty.valueOf()] = filterStateDraft[columnFilterProperty.valueOf()].filter(
    (filterValue: { id: string | number }) => filterValue.id !== columnName,
  );
};

const updateColumnFilter = <T,>(
  columnName: keyof TableColumnIds,
  filterStateDraft: WritableDraft<ITableState<T>>,
  columnFilterProperty: ColumnFilterStateProperty,
  filterValue: string | number[],
  isInternal = false,
) => {
  clearColumnFilter(columnName, filterStateDraft, columnFilterProperty);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  filterStateDraft[columnFilterProperty.valueOf()].push({
    id: columnName,
    internal: isInternal,
    value: filterValue,
  });
};

const AvailablePreviewList = <T,>(props: IAvailablePreviewListProps<T>): React.JSX.Element => {
  const {
    availableColumns,
    availableColumnsVisibility,
    availableItems,
    availableItemsColumnFilters = [],
    availableItemsPrimaryColumnId,
    availableItemsTableLabel,
    availableItemsTableNoDataFoundMessage,
    availableRowUniqueKey,
    cellContentInjector,
    injectedContentOverridesText = false,
    isLoading = false,
    isOpen,
    onItemsChanged,
    overrideSorting = false,
    previewColumns,
    previewColumnsVisibility,
    previewItems,
    previewItemsPrimaryColumnId,
    previewItemsTableLabel,
    previewRowUniqueKey,
    primaryAvailableColumnIndex,
    primaryPreviewColumnIndex,
    showColorPicker = false,
    showSideControls = false,
    tableColumnIds,
    topPosition,
  } = props;

  const [tableState, updateTableState] = useImmer<ITableState<T>>({
    availableColumns: availableColumns ?? [],
    availableItemsColumnFilters,
    previewColumns: previewColumns ?? [],
    previewItemsColumnFilters: [],
    selectedAvailableRowIds: new Set<number | string>(),
    selectedPreviewRowIds: new Set<number | string>(),
    tableOrderDesc: { availableItemsDesc: false, previewItemsDesc: false },
    unfilteredViewIds: [],
  });

  const {
    uiState: { mode },
  } = useAppSelector((state) => state.viewEditor);

  const isViewCreation = React.useMemo(() => mode === ViewEditorDrawerMode.CREATE, [mode]);

  const prevPreviewItemsDesc = React.useRef(tableState.tableOrderDesc.previewItemsDesc);

  // These refs are used to store the table instances for the available and preview items tables
  // React Table is handling the filtering, so we can just sneakily grab the filtered rows from the table instance
  // instead of managing the filtered rows ourselves
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const availableItemsTableRef = React.useRef<Table<any> | undefined>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const previewItemsTableRef = React.useRef<Table<any> | undefined>();

  const prevAvailableItemsColumnFilters = React.useRef(availableItemsColumnFilters);

  const lastClickedAvailableRowId = React.useRef<UniqueId>();
  const lastClickedPreviewRowId = React.useRef<UniqueId>();

  const rowClasses = useSharedRowStyles();
  const previewClasses = usePreviewRowStyle();

  const sortPreviewItems = React.useCallback(
    (a: string, b: string) => {
      return (
        SortingUtils.sortStrings(a, b) *
        (tableState.tableOrderDesc.previewItemsDesc
          ? DEFAULT_STRING_ARRAY_COMPARE_ASCENDING
          : DEFAULT_STRING_ARRAY_COMPARE_DESCENDING)
      );
    },
    [tableState.tableOrderDesc.previewItemsDesc],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getDisplayName = (row: any) => {
    const displayName = row.original.personnelName?.display || row.original.displayName || row.original.name;
    return displayName.toLowerCase();
  };

  /* --- Available Table Header handlers [START] --- */
  const handleAddAllAvailableItems = React.useCallback(() => {
    // If there are available items
    if (availableItems.size) {
      // If we have filters, only move items that match the filters
      if (tableState.availableItemsColumnFilters.length && availableItemsTableRef?.current) {
        let matchingRows = availableItemsTableRef.current.getFilteredRowModel().rows;
        // if in view creation mode, sort the available items
        if (isViewCreation) {
          matchingRows = matchingRows.sort((a, b) => {
            const nameA = getDisplayName(a);
            const nameB = getDisplayName(b);

            return sortPreviewItems(nameA, nameB);
          });
        }

        const updatedItems = new Map(previewItems);

        for (const row of matchingRows) {
          const item = availableItems.get(row.id);

          if (!item) continue;

          updatedItems.set(row.id, item);
        }

        onItemsChanged([...updatedItems.values()]);
      } else {
        // If we don't have filters, move all available items to preview items
        const updatedItems = produce(previewItems, (draft) => {
          let previewItemsArray = [...availableItems.entries()];
          // if we are in view creation mode, sort the items by name
          if (isViewCreation) {
            previewItemsArray = previewItemsArray.sort(([, a], [, b]) => {
              const rowA = { original: a };
              const rowB = { original: b };
              return sortPreviewItems(getDisplayName(rowA), getDisplayName(rowB));
            });
          }

          for (const [k, v] of previewItemsArray) {
            const id = getId(k);
            draft.set(id, v as Draft<APLItem<T>>);
          }
        });

        onItemsChanged([...updatedItems.values()]);
      }
    }
  }, [
    availableItems,
    isViewCreation,
    onItemsChanged,
    previewItems,
    sortPreviewItems,
    tableState.availableItemsColumnFilters.length,
  ]);

  const handleAddSelectedAvailableItems = React.useCallback(() => {
    // If there are available items
    if (availableItems.size) {
      if (availableItemsTableRef?.current) {
        let matchingRows = availableItemsTableRef.current
          .getFilteredRowModel()
          .rows.filter((item) => tableState.selectedAvailableRowIds.has(item.id));

        if (isViewCreation) {
          matchingRows = matchingRows.sort((a, b) => {
            const nameA = getDisplayName(a);
            const nameB = getDisplayName(b);
            return sortPreviewItems(nameA, nameB);
          });
        }
        const items = new Map(previewItems);
        const idsToRemove: (string | number)[] = [];

        for (const element of matchingRows) {
          const id = getId(element.id);

          const item = availableItems.get(id);

          if (!item) continue;

          items.set(id, item);
          idsToRemove.push(id);
        }

        updateTableState((draft) => {
          idsToRemove.forEach((id) => {
            draft.selectedAvailableRowIds.delete(id);
          });
        });

        onItemsChanged([...items.values()]);
      }
    }
  }, [
    availableItems,
    isViewCreation,
    onItemsChanged,
    previewItems,
    sortPreviewItems,
    tableState.selectedAvailableRowIds,
    updateTableState,
  ]);

  const handleSelectAllAvailableItems = React.useCallback(() => {
    // Handle select all available items
    if (tableState.availableItemsColumnFilters.length && availableItemsTableRef?.current) {
      const matchingRows = availableItemsTableRef.current.getFilteredRowModel()?.rowsById;

      updateTableState((draft) => {
        for (const key of Object.keys(matchingRows)) {
          const id = getId(key);

          draft.selectedAvailableRowIds.add(id);
        }
      });
    } else {
      // If we do not have any filters, select all available items
      updateTableState((draft) => {
        for (const key of availableItems.keys()) {
          const id = getId(key);

          draft.selectedAvailableRowIds.add(id);
        }
      });
    }
  }, [availableItems, tableState.availableItemsColumnFilters.length, updateTableState]);

  const handleClearSelectedAvailableItems = React.useCallback(() => {
    if (tableState.availableItemsColumnFilters.length && availableItemsTableRef?.current) {
      const matchingRows = availableItemsTableRef.current.getFilteredRowModel()?.rowsById;

      updateTableState((draft) => {
        for (const key of Object.keys(matchingRows)) {
          const id = getId(key);

          draft.selectedAvailableRowIds.delete(id);
        }
      });
    } else {
      updateTableState((draft) => {
        draft.selectedAvailableRowIds.clear();
      });
    }
  }, [tableState.availableItemsColumnFilters.length, updateTableState]);

  // Handles moving a row from available to preview
  const handleAvailableItemClick: (uniqueId: UniqueId) => void = React.useCallback(
    (uniqueId: UniqueId) => {
      const id = getId(uniqueId);

      const items = new Map(previewItems);

      const item = availableItems.get(id);

      if (!item) return;

      items.set(id, item);

      updateTableState((draft) => {
        draft.selectedAvailableRowIds.delete(id);
      });

      onItemsChanged([...items.values()]);
    },
    [availableItems, onItemsChanged, previewItems, updateTableState],
  );

  // Handles row selection (highlighting)
  const handleAvailableRowClick = React.useCallback(
    (e: React.MouseEvent, uniqueId: UniqueId) => {
      // If shift is pressed, and we have a previously selected row
      // then we need to apply the multi-select logic
      if (e.getModifierState('Shift') && lastClickedAvailableRowId.current) {
        // Find the index of the last selected item in the table row state
        // We use the table row model because Tanstack Table automatically
        // keeps the order of the row state synced with the UI order. The
        // order is maintained across sorting and filtering functions. The row model
        // also updates to only include rows that match our current filter.
        const visibleRows = availableItemsTableRef.current
          ?.getRowModel()
          .rows;
        const lastClickedRowIndex = visibleRows?.findIndex((row) => row.id === lastClickedAvailableRowId.current);
        const currentClickedRowIndex = visibleRows?.findIndex((row)=>row.id === uniqueId);

        // eslint-disable-next-line no-magic-numbers
        if (lastClickedRowIndex === undefined || lastClickedRowIndex < 0) {
          lastClickedAvailableRowId.current = uniqueId;

          return;
        }

        // Ensure the previously clicked row has not been filtered or removed
        if (lastClickedRowIndex !== currentClickedRowIndex) {
          // currentClickedRowIndex has to exist because the user can see it to click it
          const lastIndexIsBeforeCurrent = lastClickedRowIndex < (currentClickedRowIndex as number);
          let startIndex = lastClickedRowIndex;
          let stopIndex = currentClickedRowIndex as number;

          // Swap iteration bounds
          if (!lastIndexIsBeforeCurrent) {
            [startIndex, stopIndex] = [stopIndex, startIndex];
          }

          updateTableState((draft) => {
            const lastClickedId = getId(lastClickedAvailableRowId.current as UniqueId);
            const updatedSelectedAvailableRowIds = new Set(draft.selectedAvailableRowIds);
            const lastClickedItemIsHighlighted = updatedSelectedAvailableRowIds.has(lastClickedId);

            // If last clicked item is highlighted, then highlight all items between
            // current and last. Otherwise, deselect all items between current and last.
            if (!visibleRows) return;

            for (let i = startIndex; i <= stopIndex; i++) {
              const rowId = visibleRows[i].id;

              if (updatedSelectedAvailableRowIds.has(rowId)) {
                if (!lastClickedItemIsHighlighted) {
                  updatedSelectedAvailableRowIds.delete(rowId);
                }
              } else if (!updatedSelectedAvailableRowIds.has(rowId) && lastClickedItemIsHighlighted) {
                updatedSelectedAvailableRowIds.add(rowId);
              }
            }

            draft.selectedAvailableRowIds = updatedSelectedAvailableRowIds;
          });
        } else {
          // The last clicked row does not exist in our current table context or is the same
          // therefore, set the id to the current row
          lastClickedAvailableRowId.current = uniqueId;
        }

        return;
      }

      const id = getId(uniqueId);
      updateTableState((draft) => {
        const updatedSelectedAvailableItemIdsRowIds = new Set(draft.selectedAvailableRowIds);

        if (updatedSelectedAvailableItemIdsRowIds.has(id)) {
          updatedSelectedAvailableItemIdsRowIds.delete(id);
        } else {
          updatedSelectedAvailableItemIdsRowIds.add(id);
        }

        draft.selectedAvailableRowIds = updatedSelectedAvailableItemIdsRowIds;
      });

      lastClickedAvailableRowId.current = uniqueId;
    },
    [updateTableState],
  );

  /* --- Available Table Header handlers [END] --- */

  /* --- Preview Table Header handlers [START] --- */
  const handleRemoveAllPreviewItems = React.useCallback(() => {
    // If there are available items
    if (previewItems.size) {
      // If we have filters, only move items that match the filters
      if (tableState.previewItemsColumnFilters.length && previewItemsTableRef?.current) {
        const matchingRows = previewItemsTableRef.current.getFilteredRowModel()?.rowsById;

        const items = new Map(previewItems);

        for (const key of Object.keys(matchingRows)) {
          const id = getId(key);

          const item = { ...previewItems.get(id) };

          if (!item) return;

          items.delete(id);
        }

        onItemsChanged([...items.values()]);
      } else {
        // If we don't have filters, move all available items to preview items
        onItemsChanged([]);
      }
    }
  }, [onItemsChanged, previewItems, tableState.previewItemsColumnFilters.length]);

  const handleSelectAllPreviewItems = React.useCallback(() => {
    // Handle select all available items
    if (tableState.previewItemsColumnFilters.length && previewItemsTableRef?.current) {
      const matchingRows = previewItemsTableRef.current.getFilteredRowModel()?.rowsById;

      updateTableState((draft) => {
        for (const key of Object.keys(matchingRows)) {
          const id = getId(key);

          draft.selectedPreviewRowIds.add(id);
        }
      });
    } else {
      // If we do not have any filters, select all available items
      updateTableState((draft) => {
        for (const key of previewItems.keys()) {
          const id = getId(key);

          draft.selectedPreviewRowIds.add(id);
        }
      });
    }
  }, [previewItems, tableState.previewItemsColumnFilters.length, updateTableState]);

  const handleClearSelectedPreviewItems = React.useCallback(() => {
    if (tableState.previewItemsColumnFilters.length && previewItemsTableRef?.current) {
      const matchingRows = previewItemsTableRef.current.getFilteredRowModel()?.rowsById;

      updateTableState((draft) => {
        for (const key of Object.keys(matchingRows)) {
          const id = getId(key);

          draft.selectedPreviewRowIds.delete(id);
        }
      });
    }
    updateTableState((draft) => {
      draft.selectedPreviewRowIds.clear();
    });
  }, [tableState.previewItemsColumnFilters.length, updateTableState]);

  const handleRemoveSelectedPreviewItems = React.useCallback(() => {
    // If there are preview items
    if (previewItems.size) {
      if (previewItemsTableRef?.current) {
        const matchingRows = previewItemsTableRef.current
          .getFilteredRowModel()
          .rows.filter((item) => tableState.selectedPreviewRowIds.has(item.id));

        const items = new Map(previewItems);
        const nextSelectedPreviewRowIds = new Set(tableState.selectedPreviewRowIds);

        matchingRows.forEach((element) => {
          const id = getId(element.id);

          const item = { ...items.get(id) };

          if (!item) return;

          nextSelectedPreviewRowIds.delete(id);
          items.delete(id);
        });

        onItemsChanged([...items.values()]);

        updateTableState((draft) => {
          draft.selectedPreviewRowIds = nextSelectedPreviewRowIds;
        });
      }
    }
  }, [onItemsChanged, previewItems, tableState.selectedPreviewRowIds, updateTableState]);

  // Handles row selection (highlighting)
  const handlePreviewRowClick = React.useCallback(
    (e: React.MouseEvent, uniqueId: UniqueId) => {
      // If shift is pressed, and we have a previously selected row
      // then we need to apply the multi-select logic
      if (e.getModifierState('Shift') && lastClickedPreviewRowId.current) {
        // Find the index of the last selected item in the table row state
        // We use the table row model because Tanstack Table automatically
        // keeps the order of the row state synced with the UI order. The
        // order is maintained across sorting and filtering functions. The row model
        // also updates to only include rows that match our current filter.
        const visibleRows = previewItemsTableRef.current
          ?.getRowModel()
          .rows;
        const lastClickedRowIndex = visibleRows?.findIndex((row)=>row.id === lastClickedPreviewRowId.current);
        const currentClickedRowIndex = visibleRows?.findIndex((row)=>row.id === uniqueId);

        // eslint-disable-next-line no-magic-numbers
        if (lastClickedRowIndex === undefined || lastClickedRowIndex < 0) {
          lastClickedPreviewRowId.current = uniqueId;

          return;
        }

        // Ensure the previously clicked row has not been filtered or removed
        if (lastClickedRowIndex !== currentClickedRowIndex) {
          // currentClickedRowIndex has to exist because the user can see it to click it
          const lastIndexIsBeforeCurrent = lastClickedRowIndex < (currentClickedRowIndex as number);
          let startIndex = lastClickedRowIndex;
          let stopIndex = currentClickedRowIndex as number;

          // Swap iteration bounds
          if (!lastIndexIsBeforeCurrent) {
            [startIndex, stopIndex] = [stopIndex, startIndex];
          }

          updateTableState((draft) => {
            const lastClickedId = getId(lastClickedPreviewRowId.current as UniqueId);
            const updatedSelectedPreviewRowIds = new Set(draft.selectedPreviewRowIds);
            const lastClickedItemIsHighlighted = updatedSelectedPreviewRowIds.has(lastClickedId);

            // If last clicked item is highlighted, then highlight all items between
            // current and last. Otherwise, deselect all items between current and last.

            if (!visibleRows) return;

            for (let i = startIndex; i <= stopIndex; i++) {
              const rowId = visibleRows[i].id;

              if (updatedSelectedPreviewRowIds.has(rowId)) {
                if (!lastClickedItemIsHighlighted) {
                  updatedSelectedPreviewRowIds.delete(rowId);
                }
              } else if (!updatedSelectedPreviewRowIds.has(rowId) && lastClickedItemIsHighlighted) {
                updatedSelectedPreviewRowIds.add(rowId);
              }
            }

            draft.selectedPreviewRowIds = updatedSelectedPreviewRowIds;
          });
        } else {
          // The last clicked row does not exist in our current table context or is the same
          // therefore, set the id to the current row
          lastClickedPreviewRowId.current = uniqueId;
        }

        return;
      }

      updateTableState((draft) => {
        const id = getId(uniqueId);
        const updatedSelectedPreviewRowIds = new Set(draft.selectedPreviewRowIds);

        if (updatedSelectedPreviewRowIds.has(id)) {
          updatedSelectedPreviewRowIds.delete(id);
        } else {
          updatedSelectedPreviewRowIds.add(id);
        }

        draft.selectedPreviewRowIds = updatedSelectedPreviewRowIds;
      });

      lastClickedPreviewRowId.current = uniqueId;
    },
    [updateTableState],
  );

  // Handles moving a row from preview to available
  const handlePreviewItemClick = React.useCallback(
    (uniqueId: UniqueId) => {
      const id = getId(uniqueId);

      const items = new Map(previewItems);

      items.delete(id);
      onItemsChanged([...items.values()]);

      updateTableState((draft) => {
        const item = items.get(id);

        if (!item) return;

        if (Object.hasOwn(item, 'color')) {
          item.color = null;
          item.colorText = null;
        }

        draft.selectedPreviewRowIds.delete(id);
      });
    },
    [onItemsChanged, previewItems, updateTableState],
  );

  /* --- Preview Table Header handlers [END] --- */

  React.useEffect(() => {
    if (_.isEmpty(availableItemsColumnFilters) && _.isEmpty(tableState.availableItemsColumnFilters)) return;

    if (_.isEqual(availableItemsColumnFilters, prevAvailableItemsColumnFilters.current)) return;

    prevAvailableItemsColumnFilters.current = availableItemsColumnFilters;

    updateTableState((draft) => {
      const internalFilters = draft.availableItemsColumnFilters.filter(({ internal }) => internal);
      draft.availableItemsColumnFilters = [...internalFilters, ...availableItemsColumnFilters];
    });
  }, [availableItemsColumnFilters, tableState.availableItemsColumnFilters, updateTableState]);

  // Available views select all button on the table header
  const getAvailableItemsTableHeaderButton = () => {
    return (
      <TableHeaderSelectAll
        handleAddAll={handleAddAllAvailableItems}
        handleClearSelection={handleClearSelectedAvailableItems}
        handleSelectAll={handleSelectAllAvailableItems}
        handleAddSelected={handleAddSelectedAvailableItems}
        isDisabled={!availableItems.size}
        hasSelectedItems={!!tableState.selectedAvailableRowIds.size}
      />
    );
  };

  // Available views select all button on the table header
  const getPreviewItemsTableHeaderButton = () => {
    return (
      <PreviewTableHeader
        handleClearSelection={handleClearSelectedPreviewItems}
        handleSelectAll={handleSelectAllPreviewItems}
        handleRemoveSelected={handleRemoveSelectedPreviewItems}
        handleRemoveAll={handleRemoveAllPreviewItems}
        isDisabled={!previewItems.size}
        hasSelectedItems={!!tableState.selectedPreviewRowIds.size}
      />
    );
  };

  // Selected views search bar
  const getPreviewTableHeader = () => {
    return (
      <TableHeader
        handleFilter={(e) => {
          updateTableState((draft) => {
            updateColumnFilter(
              tableColumnIds[previewItemsPrimaryColumnId],
              draft,
              ColumnFilterStateProperty.PreviewItemsColumnFilters,
              e.target.value,
              true,
            );
          });
        }}
        label={previewItemsTableLabel}
        placeholder="Search Selected"
      />
    );
  };

  // Available views search bar
  const AvailableItemsTableHeader = React.useMemo(() => {
    return (
      <TableHeader
        handleFilter={(e) => {
          updateTableState((draft) => {
            updateColumnFilter(
              tableColumnIds[availableItemsPrimaryColumnId],
              draft,
              ColumnFilterStateProperty.AvailableItemsColumnFilters,
              e.target.value,
              true,
            );
          });
        }}
        label={availableItemsTableLabel}
        placeholder={'Search Available'}
      />
    );
  }, [availableItemsPrimaryColumnId, availableItemsTableLabel, tableColumnIds, updateTableState]);

  React.useEffect(() => {
    updateTableState((draft) => {
      const currentColumns = [...availableColumns];

      const currentPrimaryColumnCell = (item: CellContext<APLItem<T>, unknown>) =>
        cellFactory<APLItem<T>>({
          actionIcon: <FaPlus className={previewClasses.icon} />,
          actionIconClickHandler: handleAvailableItemClick,
          cellContentInjector,
          cellContext: item,
          className: rowClasses.row,
          clickHandler: handleAvailableRowClick,
          injectedContentOverridesText,
          isSelected: tableState.selectedAvailableRowIds.has(item.row.original.id),
          text: item.row.original.name,
        });
      currentColumns[primaryAvailableColumnIndex] = {
        ...currentColumns[primaryAvailableColumnIndex],
        cell: currentPrimaryColumnCell,
      };

      draft.availableColumns = currentColumns;
    });
  }, [
    availableColumns,
    cellContentInjector,
    handleAvailableRowClick,
    handleAvailableItemClick,
    injectedContentOverridesText,
    previewClasses.icon,
    primaryAvailableColumnIndex,
    rowClasses.row,
    tableState.selectedAvailableRowIds,
    updateTableState,
  ]);

  React.useEffect(() => {
    updateTableState((draft) => {
      const currentColumns = [...previewColumns];

      const currentPrimaryColumnCell = (item: CellContext<APLItem<T>, unknown>) =>
        cellFactory<APLItem<T>>({
          actionIcon: <FaMinus className={`${previewClasses.icon} delete`} />,
          actionIconClickHandler: handlePreviewItemClick,
          cellContentInjector,
          cellContext: item,
          className: rowClasses.row,
          clickHandler: handlePreviewRowClick,
          injectedContentOverridesText,
          isSelected: tableState.selectedPreviewRowIds.has(item.row.original.id),
          text: item.row.original.name,
        });
      currentColumns[primaryPreviewColumnIndex] = {
        ...currentColumns[primaryPreviewColumnIndex],
        cell: currentPrimaryColumnCell,
      };

      draft.previewColumns = currentColumns;
    });
  }, [
    cellContentInjector,
    handlePreviewItemClick,
    handlePreviewRowClick,
    injectedContentOverridesText,
    previewClasses.icon,
    previewColumns,
    primaryPreviewColumnIndex,
    rowClasses.row,
    tableState.selectedPreviewRowIds,
    updateTableState,
  ]);

  React.useEffect(() => {
    if (prevPreviewItemsDesc.current !== tableState.tableOrderDesc.previewItemsDesc) {
      prevPreviewItemsDesc.current = tableState.tableOrderDesc.previewItemsDesc;
    } else {
      return;
    }

    if (overrideSorting) {
      const sortedPreviewItems = [...previewItems.values()].sort((a, b) => {
        return (
          SortingUtils.sortStrings(a['name'], b['name']) *
          (tableState.tableOrderDesc.previewItemsDesc
            ? DEFAULT_STRING_ARRAY_COMPARE_ASCENDING
            : DEFAULT_STRING_ARRAY_COMPARE_DESCENDING)
        );
      });

      onItemsChanged?.(sortedPreviewItems);
    }
  }, [overrideSorting, onItemsChanged, tableState.tableOrderDesc.previewItemsDesc, previewItems]);

  const handleSetCellColors = React.useCallback(
    (colors: CellColorUpdate) => {
      if (!colors || !tableState.selectedPreviewRowIds.size || (!colors.cellColorChanged && !colors.textColorChanged))
        return;

      const updatedPreviewItems = new Map(previewItems);

      tableState.selectedPreviewRowIds.forEach((id) => {
        const item = updatedPreviewItems.get(id);

        if (!item) return;

        // Only update the color if there are changes made
        updatedPreviewItems.set(id, {
          ...item,
          color: colors.cellColorChanged ? colors.cellColor : item.color,
          colorText: colors.textColorChanged ? colors.textColor : item.colorText,
        });
      });

      onItemsChanged?.([...updatedPreviewItems.values()]);
    },
    [tableState.selectedPreviewRowIds, previewItems, onItemsChanged],
  );

  const handleUpdate = React.useCallback(
    (result: unknown) => {
      const updatedItems = new Map((result as APLItem<T>[]).map((item: APLItem<T>) => [item.id, item]));

      onItemsChanged([...updatedItems.values()]);
    },
    [onItemsChanged],
  );

  const selectedPreviewItems = React.useMemo(() => {
    return [...previewItems.values()].filter((item) => tableState.selectedPreviewRowIds.has(item.id));
  }, [previewItems, tableState.selectedPreviewRowIds]);

  // The last clicked item should be cleared when the
  // table filters change to maintain parity with legacy VM
  React.useEffect(()=>{
    lastClickedPreviewRowId.current = undefined;
  },[tableState.previewItemsColumnFilters]);

  React.useEffect(()=>{
    lastClickedAvailableRowId.current = undefined;
  },[tableState.availableItemsColumnFilters]);

  if (!isOpen) return <></>;

  return (
    <PaginatedPreviewTable
      availableItemsTableColumnFilters={[...tableState.availableItemsColumnFilters]}
      availableItemsTableColumns={tableState.availableColumns}
      availableItemsTableColumnVisibility={availableColumnsVisibility}
      availableItemsTableData={[...availableItems.values()]}
      availableItemsTableHeader={AvailableItemsTableHeader}
      availableItemsTableHeaderButton={getAvailableItemsTableHeaderButton()}
      availableItemsTableInstanceRef={availableItemsTableRef}
      availableItemsTableNoDataFoundMessage={availableItemsTableNoDataFoundMessage}
      availableTableUniqueSelectedIds={tableState.selectedAvailableRowIds}
      availableItemsTableSortingState={[
        {
          desc: tableState.tableOrderDesc.availableItemsDesc,
          id: tableColumnIds[availableItemsPrimaryColumnId],
        },
      ]}
      availableRowUniqueKey={availableRowUniqueKey}
      handleSetCellColors={handleSetCellColors}
      headerSortHandlers={[
        {
          columnId: tableColumnIds[availableItemsPrimaryColumnId],
          sortHandler: () =>
            updateTableState((draft) => {
              draft.tableOrderDesc = {
                ...draft.tableOrderDesc,
                availableItemsDesc: !draft.tableOrderDesc.availableItemsDesc,
              };
            }),
        },
      ]}
      isLoading={isLoading}
      overrideTableSorting={overrideSorting}
      previewHeaderSortHandlers={[
        {
          columnId: tableColumnIds[previewItemsPrimaryColumnId],
          sortHandler: () =>
            updateTableState((draft) => {
              draft.tableOrderDesc = {
                ...draft.tableOrderDesc,
                previewItemsDesc: !draft.tableOrderDesc.previewItemsDesc,
              };
            }),
        },
      ]}
      previewRowUniqueKey={previewRowUniqueKey}
      previewTableColumnFilters={tableState.previewItemsColumnFilters}
      previewTableColumns={tableState.previewColumns}
      previewTableColumnVisibility={previewColumnsVisibility}
      previewTableData={[...previewItems.values()]}
      previewTableHeader={getPreviewTableHeader()}
      previewTableHeaderButton={getPreviewItemsTableHeaderButton()}
      previewItemsTableInstanceRef={previewItemsTableRef}
      previewTableSortingState={[
        {
          desc: tableState.tableOrderDesc.previewItemsDesc,
          id: tableColumnIds[previewItemsPrimaryColumnId],
        },
      ]}
      previewTableUniqueSelectedIds={tableState.selectedPreviewRowIds}
      previewTableUpdateHandler={handleUpdate}
      selectedPreviewItems={selectedPreviewItems}
      showColorPicker={showColorPicker}
      showSideControls={showSideControls}
      topPosition={topPosition}
    />
  );
};

export default AvailablePreviewList;
